import React, { useState, useEffect } from 'react';
import { 
  Table, Button, Space, Popconfirm, message, Modal, Form, Input, 
  InputNumber, Select, Upload, Switch, Card, Row, Col, Tabs, 
  Image, Tag, Spin, Divider, Descriptions, Badge
} from 'antd';
import { 
  PlusOutlined, EditOutlined, DeleteOutlined, 
  PictureOutlined, VideoCameraOutlined, AppstoreOutlined,
  UploadOutlined, ReloadOutlined, FilterOutlined, 
  SearchOutlined, ClearOutlined
} from '@ant-design/icons';
import { 
  getProducts, adminDeleteProduct, adminCreateProduct, adminUpdateProduct,
  getMainCategories, getSubCategories, getColors, getQualities, getSizes,
  adminCreateVariant, adminUpdateVariant, adminDeleteVariant,
  adminCreateVariantSize, adminUpdateVariantSize, adminDeleteVariantSize,
  adminAddProductImage, adminDeleteProductImage,
  adminCreateFeature, adminUpdateFeature, adminDeleteFeature,
  getProductBySlug
} from '../../services/productApi';
import { useNotificationContext } from '../../components/NotificationContext';
import { BaseUrlAddress } from '../../components/Initilization';

const { TextArea } = Input;
const { Option } = Select;
const { TabPane } = Tabs;

const AdminProducts = () => {
  const notif = useNotificationContext();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [form] = Form.useForm();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  
  // Filter states
  const [filters, setFilters] = useState({
    search: '',
    main_category: null,
    sub_category: null,
    is_available: null,
    is_featured: null,
    is_new: null,
    is_best_seller: null,
    is_orderable: null,
    min_price: null,
    max_price: null
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Reference data
  const [mainCategories, setMainCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [colors, setColors] = useState([]);
  const [qualities, setQualities] = useState([]);
  const [allSizes, setAllSizes] = useState({});
  
  // Selected product for detailed editing
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedProductDetail, setSelectedProductDetail] = useState(null);
  const [variants, setVariants] = useState([]);
  const [variantModalVisible, setVariantModalVisible] = useState(false);
  const [editingVariant, setEditingVariant] = useState(null);
  const [variantForm] = Form.useForm();
  
  const [sizeModalVisible, setSizeModalVisible] = useState(false);
  const [editingSize, setEditingSize] = useState(null);
  const [selectedVariantForSize, setSelectedVariantForSize] = useState(null);
  const [sizeForm] = Form.useForm();
  
  const [imageModalVisible, setImageModalVisible] = useState(false);
  const [imageForm] = Form.useForm();
  
  const [featureModalVisible, setFeatureModalVisible] = useState(false);
  const [editingFeature, setEditingFeature] = useState(null);
  const [featureForm] = Form.useForm();
  
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    fetchProducts(1, 10);
    fetchReferenceData();
  }, []);

  const fetchProducts = async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const params = {
        page,
        page_size: pageSize,
        ...filters
      };
      
      const response = await getProducts(params);
      console.log('Products API response:', response);
      
      let data = [];
      let total = 0;
      
      if (response.data?.results) {
        data = response.data.results;
        total = response.data.count;
      } 
      else if (response.data?.ObjList) {
        data = response.data.ObjList;
        total = data.length;
      } 
      else if (Array.isArray(response.data)) {
        data = response.data;
        total = data.length;
      } 
      else if (response.data?.data && Array.isArray(response.data.data)) {
        data = response.data.data;
        total = data.length;
      } 
      else {
        data = [];
        total = 0;
      }
      
      if (!Array.isArray(data)) {
        console.error('Products data is not an array:', data);
        data = [];
      }
      
      setProducts(data);
      setPagination({ current: page, pageSize, total });
    } catch (error) {
      console.error('Error fetching products:', error);
      message.error('خطا در دریافت محصولات');
      setProducts([]);
      setPagination({ current: 1, pageSize: 10, total: 0 });
    } finally {
      setLoading(false);
    }
  };

  const fetchReferenceData = async () => {
    try {
      const [catsRes, colorsRes, qualitiesRes] = await Promise.all([
        getMainCategories(),
        getColors(),
        getQualities()
      ]);
      
      setMainCategories(catsRes.data?.ObjList || catsRes.data || []);
      setColors(colorsRes.data?.ObjList || colorsRes.data || []);
      setQualities(qualitiesRes.data?.ObjList || qualitiesRes.data || []);
      
      const sizeTypes = ['men_tshirt', 'men_collar', 'men_shirt', 'men_hoodie', 'women_tshirt', 'women_collar', 'women_crop', 'kid_tshirt', 'kid_collar', 'kid_shirt', 'kid_hoodie'];
      const sizesMap = {};
      for (const type of sizeTypes) {
        try {
          const res = await getSizes(type);
          sizesMap[type] = res.data?.ObjList || res.data || [];
        } catch (e) {
          sizesMap[type] = [];
        }
      }
      setAllSizes(sizesMap);
    } catch (error) {
      console.error('Error fetching reference data:', error);
    }
  };

  const handleMainCategoryChange = async (categoryId) => {
    try {
      const response = await getSubCategories(categoryId);
      setSubCategories(response.data?.ObjList || response.data || []);
    } catch (error) {
      message.error('خطا در دریافت زیردسته‌ها');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    fetchProducts(1, pagination.pageSize);
  };

  const resetFilters = () => {
    setFilters({
      search: '',
      main_category: null,
      sub_category: null,
      is_available: null,
      is_featured: null,
      is_new: null,
      is_best_seller: null,
      is_orderable: null,
      min_price: null,
      max_price: null
    });
    setSubCategories([]);
    fetchProducts(1, pagination.pageSize);
  };

  const handleAddProduct = () => {
    setEditingProduct(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    form.setFieldsValue({
      title: product.title,
      slug: product.slug,
      main_category: product.main_category_id,
      sub_category: product.sub_category_id,
      base_price: product.base_price,
      discount_percent: product.discount_percent,
      is_available: product.is_available,
      numbers_in_stock: product.numbers_in_stock,
      is_featured: product.is_featured,
      is_best_seller: product.is_best_seller,
      is_new: product.is_new,
      is_orderable: product.is_orderable,
      short_description: product.short_description,
      meta_title: product.meta_title,
      meta_description: product.meta_description
    });
    if (product.main_category_id) {
      handleMainCategoryChange(product.main_category_id);
    }
    setModalVisible(true);
  };

  const handleDeleteProduct = async (productId) => {
    try {
      await adminDeleteProduct(productId);
      message.success('محصول با موفقیت حذف شد');
      fetchProducts(pagination.current, pagination.pageSize);
    } catch (error) {
      message.error('خطا در حذف محصول');
    }
  };

  const handleSubmitProduct = async (values) => {
    try {
      const formData = new FormData();
      Object.keys(values).forEach(key => {
        if (key === 'main_image' && values[key] && values[key].file) {
          formData.append(key, values[key].file);
        } else if (values[key] !== undefined && values[key] !== null) {
          formData.append(key, values[key]);
        }
      });
      
      if (editingProduct) {
        await adminUpdateProduct(editingProduct.id, formData);
        message.success('محصول با موفقیت ویرایش شد');
      } else {
        await adminCreateProduct(formData);
        message.success('محصول با موفقیت ایجاد شد');
      }
      setModalVisible(false);
      fetchProducts(pagination.current, pagination.pageSize);
    } catch (error) {
      console.error('Error saving product:', error);
      message.error('خطا در ذخیره محصول');
    }
  };

  const handleViewProductDetails = async (product) => {
    setSelectedProduct(product);
    setDetailLoading(true);
    setSelectedProductDetail(null);
    setVariants([]);
    
    try {
      const response = await getProductBySlug(product.slug);
      const productData = response.data?.Obj || response.data;
      setSelectedProductDetail(productData);
      setVariants(productData?.variants || []);
    } catch (error) {
      console.error('Error fetching product details:', error);
      message.error('خطا در دریافت جزئیات محصول');
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshProductDetail = async () => {
    if (!selectedProduct) return;
    setDetailLoading(true);
    try {
      const response = await getProductBySlug(selectedProduct.slug);
      const productData = response.data?.Obj || response.data;
      setSelectedProductDetail(productData);
      setVariants(productData?.variants || []);
    } catch (error) {
      console.error('Error refreshing product details:', error);
      message.error('خطا در دریافت جزئیات محصول');
    } finally {
      setDetailLoading(false);
    }
  };

  // Variant management functions (keep existing ones)
  const handleAddVariant = () => {
    setEditingVariant(null);
    variantForm.resetFields();
    variantForm.setFieldsValue({ product: selectedProduct.id });
    setVariantModalVisible(true);
  };

  const handleEditVariant = (variant) => {
    setEditingVariant(variant);
    variantForm.setFieldsValue({
      product: selectedProduct.id,
      color: variant.color,
      quality: variant.quality,
      custom_price: variant.custom_price,
      stock_quantity: variant.stock_quantity,
      is_available: variant.is_available
    });
    setVariantModalVisible(true);
  };

  const handleSubmitVariant = async (values) => {
    try {
      const formData = new FormData();
      Object.keys(values).forEach(key => {
        if (key === 'front_image' && values[key] && values[key].file) {
          formData.append(key, values[key].file);
        } else if (key === 'back_image' && values[key] && values[key].file) {
          formData.append(key, values[key].file);
        } else if (values[key] !== undefined && values[key] !== null) {
          formData.append(key, values[key]);
        }
      });
      
      if (editingVariant) {
        await adminUpdateVariant(editingVariant.id, formData);
        message.success('تنوع با موفقیت ویرایش شد');
      } else {
        await adminCreateVariant(formData);
        message.success('تنوع با موفقیت ایجاد شد');
      }
      setVariantModalVisible(false);
      await refreshProductDetail();
    } catch (error) {
      console.error('Error saving variant:', error);
      message.error('خطا در ذخیره تنوع');
    }
  };

  const handleDeleteVariant = async (variantId) => {
    try {
      await adminDeleteVariant(variantId);
      message.success('تنوع با موفقیت حذف شد');
      await refreshProductDetail();
    } catch (error) {
      console.error('Error deleting variant:', error);
      message.error('خطا در حذف تنوع');
    }
  };

  const handleAddSize = (variant) => {
    setSelectedVariantForSize(variant);
    setEditingSize(null);
    sizeForm.resetFields();
    sizeForm.setFieldsValue({ variant: variant.id });
    setSizeModalVisible(true);
  };

  const handleEditSize = (size, variant) => {
    setSelectedVariantForSize(variant);
    setEditingSize(size);
    sizeForm.setFieldsValue({
      variant: variant.id,
      size: size.size,
      stock_quantity: size.stock_quantity,
      additional_price: size.additional_price
    });
    setSizeModalVisible(true);
  };

  const handleSubmitSize = async (values) => {
    try {
      if (editingSize) {
        await adminUpdateVariantSize(editingSize.id, values);
        message.success('سایز با موفقیت ویرایش شد');
      } else {
        await adminCreateVariantSize(values);
        message.success('سایز با موفقیت اضافه شد');
      }
      setSizeModalVisible(false);
      await refreshProductDetail();
    } catch (error) {
      console.error('Error saving size:', error);
      message.error('خطا در ذخیره سایز');
    }
  };

  const handleDeleteSize = async (sizeId) => {
    try {
      await adminDeleteVariantSize(sizeId);
      message.success('سایز با موفقیت حذف شد');
      await refreshProductDetail();
    } catch (error) {
      console.error('Error deleting size:', error);
      message.error('خطا در حذف سایز');
    }
  };

  const handleAddImage = () => {
    imageForm.resetFields();
    imageForm.setFieldsValue({ product: selectedProduct.id });
    setImageModalVisible(true);
  };

  const handleSubmitImage = async (values) => {
    try {
      const formData = new FormData();
      formData.append('product', values.product);
      formData.append('image', values.image.file);
      formData.append('alt_text', values.alt_text || '');
      formData.append('display_order', values.display_order || 0);
      
      await adminAddProductImage(formData);
      message.success('تصویر با موفقیت اضافه شد');
      setImageModalVisible(false);
      await refreshProductDetail();
    } catch (error) {
      console.error('Error adding image:', error);
      message.error('خطا در اضافه کردن تصویر');
    }
  };

  const handleDeleteImage = async (imageId) => {
    try {
      await adminDeleteProductImage(imageId);
      message.success('تصویر با موفقیت حذف شد');
      await refreshProductDetail();
    } catch (error) {
      console.error('Error deleting image:', error);
      message.error('خطا در حذف تصویر');
    }
  };

  const handleAddFeature = () => {
    setEditingFeature(null);
    featureForm.resetFields();
    featureForm.setFieldsValue({ product: selectedProduct.id });
    setFeatureModalVisible(true);
  };

  const handleEditFeature = (feature) => {
    setEditingFeature(feature);
    featureForm.setFieldsValue({
      product: selectedProduct.id,
      feature_name: feature.feature_name,
      feature_value: feature.feature_value,
      display_order: feature.display_order
    });
    setFeatureModalVisible(true);
  };

  const handleSubmitFeature = async (values) => {
    try {
      if (editingFeature) {
        await adminUpdateFeature(editingFeature.id, values);
        message.success('ویژگی با موفقیت ویرایش شد');
      } else {
        await adminCreateFeature(values);
        message.success('ویژگی با موفقیت اضافه شد');
      }
      setFeatureModalVisible(false);
      await refreshProductDetail();
    } catch (error) {
      console.error('Error saving feature:', error);
      message.error('خطا در ذخیره ویژگی');
    }
  };

  const handleDeleteFeature = async (featureId) => {
    try {
      await adminDeleteFeature(featureId);
      message.success('ویژگی با موفقیت حذف شد');
      await refreshProductDetail();
    } catch (error) {
      console.error('Error deleting feature:', error);
      message.error('خطا در حذف ویژگی');
    }
  };

  const handleTableChange = (paginationConfig, filtersConfig, sorterConfig) => {
    fetchProducts(paginationConfig.current, paginationConfig.pageSize);
  };

  // Get active filter count
  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.search) count++;
    if (filters.main_category) count++;
    if (filters.sub_category) count++;
    if (filters.is_available !== null) count++;
    if (filters.is_featured !== null) count++;
    if (filters.is_new !== null) count++;
    if (filters.is_best_seller !== null) count++;
    if (filters.is_orderable !== null) count++;
    if (filters.min_price) count++;
    if (filters.max_price) count++;
    return count;
  };

  const productColumns = [
    {
      title: 'تصویر',
      dataIndex: 'main_image_url',
      key: 'image',
      render: (url) => url ? <Image src={(BaseUrlAddress + url).replace(/([^:]\/)\/+/g, '$1')} width={50} height={50} style={{ objectFit: 'cover', borderRadius: 4 }} /> : <div style={{ width: 50, height: 50, background: '#eee', borderRadius: 4 }} />,
      width: 80
    },
    {
      title: 'عنوان',
      dataIndex: 'title',
      key: 'title',
      sorter: true
    },
    {
      title: 'دسته اصلی',
      dataIndex: 'main_category_name',
      key: 'main_category'
    },
    {
      title: 'زیردسته',
      dataIndex: 'sub_category_name',
      key: 'sub_category'
    },
    {
      title: 'قیمت پایه',
      dataIndex: 'base_price',
      key: 'base_price',
      render: (price) => price?.toLocaleString() + ' تومان',
      sorter: true
    },
    {
      title: 'تخفیف',
      dataIndex: 'discount_percent',
      key: 'discount',
      render: (percent) => percent > 0 ? <Tag color="red">{percent}%</Tag> : '-'
    },
    {
      title: 'وضعیت',
      dataIndex: 'is_available',
      key: 'available',
      render: (available) => available ? <Tag color="green">موجود</Tag> : <Tag color="red">ناموجود</Tag>
    },
    {
      title: 'ویژگی‌ها',
      key: 'badges',
      render: (_, record) => (
        <Space size={4} wrap>
          {record.is_featured && <Tag color="gold">ویژه</Tag>}
          {record.is_best_seller && <Tag color="blue">پرفروش</Tag>}
          {record.is_new && <Tag color="green">جدید</Tag>}
          {!record.is_orderable && <Tag color="orange">قابل طراحی</Tag>}
        </Space>
      )
    },
    {
      title: 'عملیات',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => handleEditProduct(record)} size="small" />
          <Button icon={<AppstoreOutlined />} onClick={() => handleViewProductDetails(record)} size="small" />
          <Popconfirm title="آیا از حذف این محصول مطمئن هستید؟" onConfirm={() => handleDeleteProduct(record.id)}>
            <Button icon={<DeleteOutlined />} danger size="small" />
          </Popconfirm>
        </Space>
      )
    }
  ];

  // Variant columns for the table inside modal
  const variantColumns = [
    { title: 'رنگ', dataIndex: 'color_name', key: 'color_name' },
    { title: 'کیفیت', dataIndex: 'quality_name', key: 'quality_name' },
    { 
      title: 'قیمت', 
      dataIndex: 'price', 
      key: 'price',
      render: (price) => price?.toLocaleString() + ' تومان'
    },
    { title: 'موجودی', dataIndex: 'stock_quantity', key: 'stock_quantity' },
    {
      title: 'سایزها',
      dataIndex: 'sizes',
      key: 'sizes',
      render: (sizes, record) => (
        <Space wrap>
          <Button size="small" type="dashed" onClick={() => handleAddSize(record)}>
            <PlusOutlined /> افزودن سایز
          </Button>
          {sizes?.map(size => (
            <Tag 
              key={size.id} 
              style={{ cursor: 'pointer', marginTop: 4 }}
              onClick={() => handleEditSize(size, record)}
            >
              {size.size_name}: {size.stock_quantity} عدد
              {size.additional_price > 0 && ` (+${size.additional_price.toLocaleString()} تومان)`}
            </Tag>
          ))}
        </Space>
      )
    },
    {
      title: 'عملیات',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} size="small" onClick={() => handleEditVariant(record)} />
          <Popconfirm title="حذف تنوع؟" onConfirm={() => handleDeleteVariant(record.id)}>
            <Button icon={<DeleteOutlined />} danger size="small" />
          </Popconfirm>
        </Space>
      )
    }
  ];

  const imageColumns = [
    {
      title: 'تصویر',
      dataIndex: 'image',
      key: 'image',
      render: (url) => <Image src={(BaseUrlAddress + url).replace(/([^:]\/)\/+/g, '$1')} width={80} height={80} style={{ objectFit: 'cover', borderRadius: 4 }} />
    },
    { title: 'متن جایگزین', dataIndex: 'alt_text', key: 'alt_text' },
    { title: 'ترتیب', dataIndex: 'display_order', key: 'display_order' },
    {
      title: 'عملیات',
      key: 'actions',
      render: (_, record) => (
        <Popconfirm title="حذف تصویر؟" onConfirm={() => handleDeleteImage(record.id)}>
          <Button icon={<DeleteOutlined />} danger size="small" />
        </Popconfirm>
      )
    }
  ];

  const featureColumns = [
    { title: 'نام ویژگی', dataIndex: 'feature_name', key: 'feature_name' },
    { title: 'مقدار', dataIndex: 'feature_value', key: 'feature_value' },
    { title: 'ترتیب', dataIndex: 'display_order', key: 'display_order' },
    {
      title: 'عملیات',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} size="small" onClick={() => handleEditFeature(record)} />
          <Popconfirm title="حذف ویژگی؟" onConfirm={() => handleDeleteFeature(record.id)}>
            <Button icon={<DeleteOutlined />} danger size="small" />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: 24, direction: 'rtl' }}>
      <Card className="light-glass-effect-card shaped-border">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 16 }}>
          <h2 style={{ margin: 0 }}>مدیریت محصولات</h2>
          <Space>
            <Row gutter={[24,24]}>
              <Col span={24} lg={12}><Button 
              icon={<FilterOutlined />} 
              onClick={() => setShowFilters(!showFilters)}
              type={showFilters ? 'primary' : 'default'}
            >
              فیلترها {getActiveFilterCount() > 0 && <Badge count={getActiveFilterCount()} style={{ backgroundColor: '#a23854' }} />}
            </Button></Col>
              <Col span={24} lg={12}>  <Button type="primary" icon={<PlusOutlined />} onClick={handleAddProduct}>
              افزودن محصول جدید
            </Button></Col>
            </Row>
            
          
          </Space>
        </div>

        {/* Filters Section */}
        {showFilters && (
          <Card size="small" style={{ marginBottom: 16, background: 'rgba(162,56,84,0.05)', borderRadius: 12 }}>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={8} lg={6}>
                <Input
                  placeholder="جستجوی عنوان..."
                  prefix={<SearchOutlined />}
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  onPressEnter={applyFilters}
                  allowClear
                />
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <Select
                  placeholder="دسته اصلی"
                  allowClear
                  value={filters.main_category}
                  onChange={(value) => {
                    handleFilterChange('main_category', value);
                    handleFilterChange('sub_category', null);
                    if (value) handleMainCategoryChange(value);
                  }}
                  style={{ width: '100%' }}
                >
                  {mainCategories.map(cat => (
                    <Option key={cat.id} value={cat.id}>{cat.name_fa}</Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <Select
                  placeholder="زیردسته"
                  allowClear
                  value={filters.sub_category}
                  onChange={(value) => handleFilterChange('sub_category', value)}
                  style={{ width: '100%' }}
                  disabled={!filters.main_category}
                >
                  {subCategories.map(sub => (
                    <Option key={sub.id} value={sub.id}>{sub.name_fa}</Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <Select
                  placeholder="وضعیت موجودی"
                  allowClear
                  value={filters.is_available}
                  onChange={(value) => handleFilterChange('is_available', value)}
                  style={{ width: '100%' }}
                >
                  <Option value={true}>موجود</Option>
                  <Option value={false}>ناموجود</Option>
                </Select>
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <Select
                  placeholder="نوع محصول"
                  allowClear
                  value={filters.is_orderable}
                  onChange={(value) => handleFilterChange('is_orderable', value)}
                  style={{ width: '100%' }}
                >
                  <Option value={true}>قابل سفارش مستقیم</Option>
                  <Option value={false}>قابل طراحی</Option>
                </Select>
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <Select
                  placeholder="وضعیت ویژه"
                  allowClear
                  value={filters.is_featured}
                  onChange={(value) => handleFilterChange('is_featured', value)}
                  style={{ width: '100%' }}
                >
                  <Option value={true}>ویژه</Option>
                  <Option value={false}>عادی</Option>
                </Select>
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <Select
                  placeholder="محصول جدید"
                  allowClear
                  value={filters.is_new}
                  onChange={(value) => handleFilterChange('is_new', value)}
                  style={{ width: '100%' }}
                >
                  <Option value={true}>جدید</Option>
                  <Option value={false}>قدیمی</Option>
                </Select>
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <Select
                  placeholder="پرفروش"
                  allowClear
                  value={filters.is_best_seller}
                  onChange={(value) => handleFilterChange('is_best_seller', value)}
                  style={{ width: '100%' }}
                >
                  <Option value={true}>پرفروش</Option>
                  <Option value={false}>عادی</Option>
                </Select>
              </Col>
              <Col xs={12} md={6}>
                <InputNumber
                  placeholder="حداقل قیمت"
                  value={filters.min_price}
                  onChange={(value) => handleFilterChange('min_price', value)}
                  style={{ width: '100%' }}
                  min={0}
                />
              </Col>
              <Col xs={12} md={6}>
                <InputNumber
                  placeholder="حداکثر قیمت"
                  value={filters.max_price}
                  onChange={(value) => handleFilterChange('max_price', value)}
                  style={{ width: '100%' }}
                  min={0}
                />
              </Col>
              <Col xs={24} md={12}>
                <Space>
                  <Button type="primary" onClick={applyFilters} icon={<SearchOutlined />}>
                    اعمال فیلتر
                  </Button>
                  <Button onClick={resetFilters} icon={<ClearOutlined />}>
                    پاک کردن همه
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>
        )}
        
<Table
  columns={productColumns}
  dataSource={products}
  rowKey="id"
  loading={loading}
  scroll={{ x: 'max-content' }}
  pagination={{
    current: pagination.current,
    pageSize: pagination.pageSize,
    total: pagination.total,
    showSizeChanger: true,
    showTotal: (total) => `${total} محصول`,
    pageSizeOptions: ['10', '20', '50', '100'],
  }}
  onChange={handleTableChange}
/>
      </Card>

      {/* Rest of the modals remain the same */}
      {/* Product Create/Edit Modal */}
      <Modal
        title={editingProduct ? 'ویرایش محصول' : 'محصول جدید'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={800}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmitProduct}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="title" label="عنوان محصول" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="slug" label="slug" rules={[{ required: false }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="main_category" label="دسته اصلی" rules={[{ required: true }]}>
                <Select onChange={handleMainCategoryChange} placeholder="انتخاب دسته اصلی">
                  {mainCategories.map(cat => (
                    <Option key={cat.id} value={cat.id}>{cat.name_fa}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="sub_category" label="زیردسته" rules={[{ required: true }]}>
                <Select placeholder="انتخاب زیردسته" disabled={!subCategories.length}>
                  {subCategories.map(sub => (
                    <Option key={sub.id} value={sub.id}>{sub.name_fa}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="base_price" label="قیمت پایه (تومان)" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="discount_percent" label="درصد تخفیف">
                <InputNumber min={0} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="numbers_in_stock" label="تعداد موجودی">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="short_description" label="توضیحات کوتاه">
                <TextArea rows={2} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="description" label="توضیحات کامل">
                <TextArea rows={4} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="main_image" label="تصویر اصلی">
                <Upload beforeUpload={() => false} listType="picture-card" maxCount={1}>
                  <div><PlusOutlined /><div style={{ marginTop: 8 }}>آپلود</div></div>
                </Upload>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="is_available" valuePropName="checked" label="موجود">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="is_featured" valuePropName="checked" label="محصول ویژه">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="is_best_seller" valuePropName="checked" label="پرفروش">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="is_new" valuePropName="checked" label="جدید">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="is_orderable" valuePropName="checked" label="قابل سفارش">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="meta_title" label="عنوان SEO">
                <Input />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="meta_description" label="توضیحات SEO">
                <TextArea rows={2} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item>
                <Button type="primary" htmlType="submit">ذخیره</Button>
                <Button style={{ marginRight: 8 }} onClick={() => setModalVisible(false)}>لغو</Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Product Detail Modal with Variants, Images, Features */}
      <Modal
        title={`مدیریت جزئیات: ${selectedProduct?.title}`}
        open={selectedProduct !== null}
        onCancel={() => setSelectedProduct(null)}
        footer={null}
        width={1000}
      >
        {detailLoading ? (
          <div style={{ textAlign: 'center', padding: 50 }}>
            <Spin size="large" />
          </div>
        ) : selectedProductDetail && (
          <>
            <div style={{ marginBottom: 16, textAlign: 'left' }}>
              <Button icon={<ReloadOutlined />} onClick={refreshProductDetail} size="small">
                refresh
              </Button>
            </div>
            <Tabs defaultActiveKey="variants">
              <TabPane 
                tab={<span><AppstoreOutlined /> تنوع‌ها ({variants.length})</span>} 
                key="variants"
              >
                <Button type="dashed" icon={<PlusOutlined />} onClick={handleAddVariant} style={{ marginBottom: 16 }}>
                  افزودن تنوع جدید
                </Button>
                <Table
                  dataSource={variants}
                  rowKey="id"
                  columns={variantColumns}
                  pagination={false}
                  scroll={{ x: 800 }}
                />
              </TabPane>

              <TabPane 
                tab={<span><PictureOutlined /> تصاویر ({selectedProductDetail.images?.length || 0})</span>} 
                key="images"
              >
                <Button type="dashed" icon={<PlusOutlined />} onClick={handleAddImage} style={{ marginBottom: 16 }}>
                  افزودن تصویر جدید
                </Button>
                <Table
                  dataSource={selectedProductDetail.images || []}
                  rowKey="id"
                  columns={imageColumns}
                  pagination={false}
                />
              </TabPane>

              <TabPane 
                tab={<span>📋 ویژگی‌ها ({selectedProductDetail.features?.length || 0})</span>} 
                key="features"
              >
                <Button type="dashed" icon={<PlusOutlined />} onClick={handleAddFeature} style={{ marginBottom: 16 }}>
                  افزودن ویژگی جدید
                </Button>
                <Table
                  dataSource={selectedProductDetail.features || []}
                  rowKey="id"
                  columns={featureColumns}
                  pagination={false}
                />
              </TabPane>

              <TabPane tab="ℹ️ اطلاعات پایه" key="basic">
                <Descriptions bordered column={{ xs: 1, sm: 2 }}>
                  <Descriptions.Item label="شناسه">{selectedProductDetail.id}</Descriptions.Item>
                  <Descriptions.Item label="اسلاگ">{selectedProductDetail.slug}</Descriptions.Item>
                  <Descriptions.Item label="دسته اصلی">{selectedProductDetail.main_category?.name_fa}</Descriptions.Item>
                  <Descriptions.Item label="زیردسته">{selectedProductDetail.sub_category?.name_fa}</Descriptions.Item>
                  <Descriptions.Item label="قیمت پایه">{selectedProductDetail.base_price?.toLocaleString()} تومان</Descriptions.Item>
                  <Descriptions.Item label="قیمت نهایی">{selectedProductDetail.final_price?.toLocaleString()} تومان</Descriptions.Item>
                  <Descriptions.Item label="تخفیف">{selectedProductDetail.discount_percent}%</Descriptions.Item>
                  <Descriptions.Item label="موجودی انبار">{selectedProductDetail.numbers_in_stock}</Descriptions.Item>
                  <Descriptions.Item label="وضعیت">{selectedProductDetail.is_available ? 'موجود' : 'ناموجود'}</Descriptions.Item>
                  <Descriptions.Item label="امتیاز">{selectedProductDetail.rating} ({selectedProductDetail.review_count} نظر)</Descriptions.Item>
                  <Descriptions.Item label="تاریخ ایجاد">{new Date(selectedProductDetail.created).toLocaleDateString('fa-IR')}</Descriptions.Item>
                  <Descriptions.Item label="آخرین ویرایش">{new Date(selectedProductDetail.modified).toLocaleDateString('fa-IR')}</Descriptions.Item>
                </Descriptions>
              </TabPane>
            </Tabs>
          </>
        )}
      </Modal>

      {/* Variant Modal */}
      <Modal
        title={editingVariant ? 'ویرایش تنوع' : 'تنوع جدید'}
        open={variantModalVisible}
        onCancel={() => setVariantModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form form={variantForm} layout="vertical" onFinish={handleSubmitVariant}>
          <Form.Item name="product" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="color" label="رنگ" rules={[{ required: true }]}>
            <Select placeholder="انتخاب رنگ" showSearch>
              {colors.map(c => <Option key={c.id} value={c.id}>{c.name_fa}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="quality" label="کیفیت" rules={[{ required: true }]}>
            <Select placeholder="انتخاب کیفیت">
              {qualities.map(q => <Option key={q.id} value={q.id}>{q.name_fa}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="custom_price" label="قیمت سفارشی (اختیاری)">
            <InputNumber style={{ width: '100%' }} min={0} placeholder="در صورت خالی بودن، قیمت پایه محصول استفاده می‌شود" />
          </Form.Item>
          <Form.Item name="stock_quantity" label="تعداد موجودی">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="is_available" valuePropName="checked" label="موجود">
            <Switch />
          </Form.Item>
          <Form.Item name="front_image" label="تصویر جلو">
            <Upload beforeUpload={() => false} listType="picture-card" maxCount={1}>
              <div><PlusOutlined /></div>
            </Upload>
          </Form.Item>
          <Form.Item name="back_image" label="تصویر پشت">
            <Upload beforeUpload={() => false} listType="picture-card" maxCount={1}>
              <div><PlusOutlined /></div>
            </Upload>
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">ذخیره</Button>
            <Button style={{ marginRight: 8 }} onClick={() => setVariantModalVisible(false)}>لغو</Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Size Modal */}
      <Modal
        title={editingSize ? 'ویرایش سایز' : 'سایز جدید'}
        open={sizeModalVisible}
        onCancel={() => setSizeModalVisible(false)}
        footer={null}
      >
        <Form form={sizeForm} layout="vertical" onFinish={handleSubmitSize}>
          <Form.Item name="variant" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="size" label="سایز" rules={[{ required: true }]}>
            <Select 
              placeholder="انتخاب سایز" 
              showSearch
              optionFilterProp="children"
            >
              {selectedProductDetail?.sub_category?.size_chart_type && 
                allSizes[selectedProductDetail.sub_category.size_chart_type]?.map(s => (
                  <Option key={s.id} value={s.id}>
                    {s.name} - عرض: {s.width}cm, قد: {s.height}cm
                    {s.arm_length && `, آستین: ${s.arm_length}cm`}
                  </Option>
                ))
              }
            </Select>
          </Form.Item>
          <Form.Item name="stock_quantity" label="تعداد موجودی">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="additional_price" label="قیمت اضافه (تومان)">
            <InputNumber min={0} style={{ width: '100%' }} placeholder="قیمت اضافه برای این سایز" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">ذخیره</Button>
            <Button style={{ marginRight: 8 }} onClick={() => setSizeModalVisible(false)}>لغو</Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Image Modal */}
      <Modal
        title="افزودن تصویر"
        open={imageModalVisible}
        onCancel={() => setImageModalVisible(false)}
        footer={null}
      >
        <Form form={imageForm} layout="vertical" onFinish={handleSubmitImage}>
          <Form.Item name="product" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="image" label="تصویر" rules={[{ required: true }]}>
            <Upload beforeUpload={() => false} listType="picture-card" maxCount={1}>
              <div><PlusOutlined /><div>آپلود</div></div>
            </Upload>
          </Form.Item>
          <Form.Item name="alt_text" label="متن جایگزین">
            <Input />
          </Form.Item>
          <Form.Item name="display_order" label="ترتیب نمایش">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">ذخیره</Button>
            <Button style={{ marginRight: 8 }} onClick={() => setImageModalVisible(false)}>لغو</Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Feature Modal */}
      <Modal
        title={editingFeature ? 'ویرایش ویژگی' : 'ویژگی جدید'}
        open={featureModalVisible}
        onCancel={() => setFeatureModalVisible(false)}
        footer={null}
      >
        <Form form={featureForm} layout="vertical" onFinish={handleSubmitFeature}>
          <Form.Item name="product" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="feature_name" label="نام ویژگی" rules={[{ required: true }]}>
            <Input placeholder="مثال: جنس پارچه" />
          </Form.Item>
          <Form.Item name="feature_value" label="مقدار ویژگی" rules={[{ required: true }]}>
            <Input placeholder="مثال: 100% پنبه" />
          </Form.Item>
          <Form.Item name="display_order" label="ترتیب نمایش">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">ذخیره</Button>
            <Button style={{ marginRight: 8 }} onClick={() => setFeatureModalVisible(false)}>لغو</Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminProducts;